/**
 * Client-side Mortgage Calculator Utilities
 * Enterprise-grade financial calculations for mortgage scenarios
 */

export type DownPaymentType = 'amount' | 'percentage';

export interface MortgageCalculationResult {
  property_value: number;
  down_payment: number;
  down_payment_type: DownPaymentType;
  interest_rate: number;
  loan_term_years: number;
  start_date: string | null;
  loan_amount: number;
  monthly_payment: number;
  total_interest: number;
  total_paid: number;
}

/**
 * Calculate monthly payment (P&I) using standard mortgage formula
 */
function calculateMonthlyPayment(principal: number, annualRate: number, years: number): number {
  if (principal <= 0 || annualRate < 0 || years <= 0) {
    return 0;
  }

  const monthlyRate = annualRate / 12;
  const numberOfPayments = years * 12;

  if (monthlyRate === 0) {
    return principal / numberOfPayments;
  }

  const monthlyPayment =
    principal *
    (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

  return monthlyPayment;
}

/**
 * Normalize interest rate input (handles both decimal and percentage)
 */
function normalizeInterestRate(rate: number | string): number {
  const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
  
  // If rate is > 1, assume it's a percentage (e.g., 5.5 for 5.5%)
  // Otherwise assume it's a decimal (e.g., 0.055 for 5.5%)
  if (numRate > 1) {
    return numRate / 100;
  }
  return numRate;
}

/**
 * Calculate loan amount from property value and down payment
 */
function calculateLoanAmount(
  propertyValue: number,
  downPayment: number,
  downPaymentType: DownPaymentType
): number {
  let actualDownPayment: number;
  
  if (downPaymentType === 'percentage') {
    actualDownPayment = (propertyValue * downPayment) / 100;
  } else {
    actualDownPayment = downPayment;
  }

  return Math.max(0, propertyValue - actualDownPayment);
}

/**
 * Calculate all mortgage metrics
 */
export function calculateMortgage(params: {
  propertyValue: number;
  downPayment: number;
  downPaymentType: DownPaymentType;
  interestRate: number | string;
  loanTermYears: number;
  startDate?: string | null;
}): MortgageCalculationResult {
  const {
    propertyValue,
    downPayment,
    downPaymentType,
    interestRate,
    loanTermYears,
    startDate,
  } = params;

  // Normalize inputs
  const normalizedRate = normalizeInterestRate(interestRate);
  const loanAmount = calculateLoanAmount(propertyValue, downPayment, downPaymentType);
  const monthlyPayment = calculateMonthlyPayment(loanAmount, normalizedRate, loanTermYears);
  const totalPaid = monthlyPayment * loanTermYears * 12;
  const totalInterest = Math.max(0, totalPaid - loanAmount);

  return {
    property_value: propertyValue,
    down_payment: downPayment,
    down_payment_type: downPaymentType,
    interest_rate: normalizedRate,
    loan_term_years: loanTermYears,
    start_date: startDate || null,
    loan_amount: Math.round(loanAmount * 100) / 100,
    monthly_payment: Math.round(monthlyPayment * 100) / 100,
    total_interest: Math.round(totalInterest * 100) / 100,
    total_paid: Math.round(totalPaid * 100) / 100,
  };
}

